import type { AxiosError } from "axios";

/**
 * A request field that holds a secret: a password, a client or webhook secret,
 * an API key or token, a credential, a certificate. Matched on the key, at any
 * depth of the body or the query. A field that carries a secret under some other
 * name has to be added here, or it is logged.
 */
const SECRET_KEY = /pass(word|code)|secret|token|credential|certificate|private_?key|api_?key/i;

const REDACTED = "[redacted]";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Replace the value of every secret-named key. A boolean, number or null under
 * such a key stays (`is_password_autoset`, `total_output_tokens`): it cannot be
 * the secret, and it can say why a request failed.
 */
function scrubSecrets(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(scrubSecrets);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => {
      const scalar = inner === null || typeof inner === "boolean" || typeof inner === "number";
      return [key, SECRET_KEY.test(key) && !scalar ? REDACTED : scrubSecrets(inner)];
    }),
  );
}

/** The request body as sent: our client sends JSON, which axios has already stringified. */
function scrubBody(data: unknown): unknown {
  if (data === undefined || data === null) return data;
  if (typeof data === "string") {
    try {
      return scrubSecrets(JSON.parse(data));
    } catch {
      // Not JSON, so it cannot be read key by key and only its size is safe to
      // send. Not logged: this runs while the console integration serialises a
      // logged error, and a console call here would re-enter it.
      return `[non-JSON body, ${data.length} chars]`;
    }
  }
  if (isPlainObject(data) || Array.isArray(data)) return scrubSecrets(data);
  // A FormData, Blob or File upload: name the kind, never the content.
  return `[${Object.prototype.toString.call(data).slice(8, -1)}]`;
}

/**
 * What a failed request serialises to when it is logged, in place of
 * `AxiosError.toJSON()`.
 *
 * `console.warn` and `console.error` go to Sentry Logs with each argument run
 * through `JSON.stringify` at any depth, and the error-handling rule says to log
 * the caught error. So this is what triage sees: the method, URL, query and body
 * that were sent, and the server's answer, which says why it failed. The headers
 * and any secret-named field stay in the browser (NG-2189). The live error is
 * untouched, for code that reads or re-sends the request.
 */
export function loggableRequestError(error: AxiosError): Record<string, unknown> {
  const { config, response } = error;
  return {
    name: error.name,
    message: error.message,
    code: error.code,
    status: error.status ?? response?.status,
    config: config && {
      method: config.method,
      baseURL: config.baseURL,
      url: config.url,
      params: scrubSecrets(config.params),
      data: scrubBody(config.data),
    },
    // The server's error body: DRF names the failing field and the reason. Not
    // scrubbed by key, since it keys its messages by field name, and a
    // `new_password` error is the message, not the password.
    response: response && { status: response.status, data: response.data },
    stack: error.stack,
  };
}
